<template>
  <div class="signup-page">
    <spinner-loader :loadingStuff="loadingStuff" />

    <div class="signup-shell">
      <header class="signup-header">
        <TenantLogo logo-class="signup-logo" />
        <p class="signup-club">{{ clubName }}</p>
        <h1 class="signup-title">Trial sign-up</h1>
      </header>

      <!-- Invalid / used / expired invitation -->
      <section v-if="pageState === 'invalid'" class="signup-card signup-card--message">
        <h2>{{ invalidHeading }}</h2>
        <p>{{ invalidMessage }}</p>
        <p>
          Please contact the club and ask them to send you a new invitation
          link.
        </p>
      </section>

      <!-- Success -->
      <section v-else-if="pageState === 'done'" class="signup-card signup-card--message">
        <h2>You're all set!</h2>
        <p>
          Thanks — {{ formData.name || "the player" }}'s details have been sent
          to {{ clubName }}.
        </p>

        <dl class="trial-summary" v-if="invite.teamName || invite.trial_start_date">
          <template v-if="invite.teamName">
            <dt>Team</dt>
            <dd>{{ invite.teamName }}</dd>
          </template>
          <template v-if="invite.trial_start_date">
            <dt>Trial starts</dt>
            <dd>{{ formatDateForHuman(invite.trial_start_date) }}</dd>
          </template>
          <template v-if="invite.trial_length">
            <dt>Trial length</dt>
            <dd>{{ invite.trial_length }} weeks</dd>
          </template>
        </dl>

        <p>
          The club will be in touch before the first session. You can log in
          any time with the email address and password you just created to
          follow how the trial is going.
        </p>
        <router-link class="primary-button" to="/login">Go to log in</router-link>
      </section>

      <!-- Sign-up form -->
      <template v-else-if="pageState === 'form'">
        <section class="signup-card trial-card" v-if="invite.teamName || invite.trial_start_date">
          <h2>Your trial</h2>
          <dl class="trial-summary">
            <template v-if="invite.teamName">
              <dt>Team</dt>
              <dd>{{ invite.teamName }}</dd>
            </template>
            <template v-if="invite.trial_start_date">
              <dt>Starts</dt>
              <dd>{{ formatDateForHuman(invite.trial_start_date) }}</dd>
            </template>
            <template v-if="invite.trial_length">
              <dt>Length</dt>
              <dd>{{ invite.trial_length }} weeks</dd>
            </template>
          </dl>
        </section>

        <ol class="steps">
          <li v-for="(stepName, index) in steps" :key="stepName" :class="{
            'steps__item': true,
            'steps__item--active': index === currentStep,
            'steps__item--done': index < currentStep,
          }">
            {{ stepName }}
          </li>
        </ol>

        <form class="signup-card" @submit.prevent="nextStep()" novalidate>
          <!-- Step 1: Player -->
          <fieldset v-show="currentStep === 0">
            <legend>About the player</legend>

            <div class="form__item">
              <label class="form__label" for="name">Player name *</label>
              <input class="form__input" type="text" v-model="formData.name" id="name" autocomplete="name" />
              <p class="field-error" v-if="stepErrors.name">{{ stepErrors.name }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="dob">Date of birth *</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" v-model="formData.dob" name="dob" id="dob"
                :enable-time-picker="false" :max-date="new Date()" />
              <p class="field-error" v-if="stepErrors.dob">{{ stepErrors.dob }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="position">Position</label>
              <select id="position" v-model="formData.position">
                <option disabled value="">Please select a position</option>
                <option v-for="position in positions" :value="position" :key="position">
                  {{ position }}
                </option>
              </select>
            </div>

            <div class="form__item">
              <label class="form__label" for="preferredFootSelect">Preferred foot</label>
              <select v-model="formData.preferred_foot" id="preferredFootSelect">
                <option value="Right">Right</option>
                <option value="Left">Left</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div class="form__item">
              <label class="form__label" for="previous_club">Boys club (current or most recent)</label>
              <input class="form__input" type="text" v-model="formData.previous_club" id="previous_club" />
            </div>

            <ImageUploader @populateImageObjectFromUpload="imageUploadFileDataChnged($event)"
              @populateImageFileDataFromUpload="imageUploadObjectDataChanged($event)"
              @clearImageDataFromUpload="clearImageDataFromUpload()" :imageURL="formData.userImage" />

            <div class="form__item" v-if="legacyMode">
              <label class="form__label" for="teamSelect">Team *</label>
              <select v-model="formData.teamId" id="teamSelect" @change="teamSelected()">
                <option disabled value="">Please select a team</option>
                <option v-for="team in teamData" :value="team.teamId" :key="team.teamId">
                  {{ team.teamName }}
                </option>
              </select>
              <p class="field-error" v-if="stepErrors.teamId">{{ stepErrors.teamId }}</p>
            </div>

            <div class="form__item" v-if="legacyMode">
              <label class="form__label" for="trialLengthSelect">Trial length</label>
              <select v-model="formData.trial_length" id="trialLengthSelect">
                <option value="3">3 weeks</option>
                <option value="6">6 weeks</option>
                <option value="12">12 weeks</option>
              </select>
            </div>

            <div class="form__item" v-if="legacyMode">
              <label class="form__label" for="trialStartDate">Trial start date</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" v-model="formData.trial_start_date"
                name="trialStartDate" id="trialStartDate" :enable-time-picker="false" />
            </div>
          </fieldset>

          <!-- Step 2: Contact -->
          <fieldset v-show="currentStep === 1">
            <legend>Contact details</legend>

            <div class="form__item">
              <label class="form__label" for="parent_name">Parent / guardian name *</label>
              <input class="form__input" type="text" v-model="formData.parent_name" id="parent_name" />
              <p class="field-error" v-if="stepErrors.parent_name">{{ stepErrors.parent_name }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="phone">Contact phone number *</label>
              <input class="form__input" type="tel" inputmode="tel" autocomplete="tel" v-model="formData.phone"
                id="phone" />
              <p class="field-error" v-if="stepErrors.phone">{{ stepErrors.phone }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="address">Address</label>
              <textarea class="form__input form__textarea" id="address" v-model="formData.address" rows="3"
                autocomplete="street-address"></textarea>
            </div>

            <div class="form__item">
              <label class="form__label" for="post_code">Post code</label>
              <input class="form__input" type="text" v-model="formData.post_code" id="post_code"
                autocomplete="postal-code" />
            </div>

            <div class="form__item">
              <label class="form__label" for="emergency_contact_one">Emergency contact *</label>
              <input class="form__input" type="text" v-model="formData.emergency_contact_one"
                id="emergency_contact_one" placeholder="Name and phone number" />
              <p class="field-error" v-if="stepErrors.emergency_contact_one">
                {{ stepErrors.emergency_contact_one }}
              </p>
            </div>

            <div class="form__item">
              <label class="form__label" for="emergency_contact_two">Second emergency contact</label>
              <input class="form__input" type="text" v-model="formData.emergency_contact_two"
                id="emergency_contact_two" placeholder="Name and phone number" />
            </div>

            <div class="form__item">
              <label class="form__label" for="place_of_birth">Place of birth</label>
              <input class="form__input" type="text" v-model="formData.place_of_birth" id="place_of_birth" />
            </div>

            <div class="form__item">
              <label class="form__label" for="country_of_birth">Country of birth</label>
              <input class="form__input" type="text" v-model="formData.country_of_birth" id="country_of_birth" />
            </div>

            <div class="form__item">
              <label class="form__label" for="nationality">Nationality</label>
              <input class="form__input" type="text" v-model="formData.nationality" id="nationality" />
            </div>
          </fieldset>

          <!-- Step 3: Medical & consent -->
          <fieldset v-show="currentStep === 2">
            <legend>Medical &amp; consent</legend>

            <div class="form__item">
              <label class="form__label">Medical information</label>
              <MedicalConditionsEditor v-model="formData.medicalInfo" />
              <p class="field-error" v-if="stepErrors.medical">{{ stepErrors.medical }}</p>
            </div>

            <div class="form__item form__item--checkbox">
              <label class="checkbox-label" for="photo_permission">
                <input type="checkbox" id="photo_permission" v-model="formData.permission" />
                I give permission for photographs of the player to be taken
                during club activities
              </label>
            </div>

            <div class="form__item form__item--checkbox">
              <label class="checkbox-label" for="guardian_confirm">
                <input type="checkbox" id="guardian_confirm" v-model="formData.guardianConfirm" />
                I confirm I am the parent or legal guardian of this player (or
                the player is 16 or over and completing this form themselves) *
              </label>
              <p class="field-error" v-if="stepErrors.guardianConfirm">
                {{ stepErrors.guardianConfirm }}
              </p>
            </div>

            <p class="field-hint">
              The details you provide are used by {{ clubName }} to run the
              trial and will be handled in line with the club's privacy
              policy.
            </p>
          </fieldset>

          <!-- Step 4: Account -->
          <fieldset v-show="currentStep === 3">
            <legend>Create your login</legend>
            <p class="field-hint">
              You'll use this to log in and follow the trial's progress.
            </p>

            <div class="form__item">
              <label class="form__label" for="email">Email address *</label>
              <input :class="stepErrors.email ? 'danger-outline form__input' : 'form__input'" type="email"
                autocomplete="email" @change="checkChangeVlaidation(formData.email, 'email')"
                v-model="formData.email" id="email" />
              <p class="field-error" v-if="stepErrors.email">{{ stepErrors.email }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="password">Password *</label>
              <div class="password-wrapper">
                <input class="form__input" :type="showPassword ? 'text' : 'password'" autocomplete="new-password"
                  v-model="formData.password" id="password" />
                <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                  {{ showPassword ? "Hide" : "Show" }}
                </button>
              </div>
              <p class="field-hint">At least 8 characters.</p>
              <p class="field-error" v-if="stepErrors.password">{{ stepErrors.password }}</p>
            </div>

            <div class="form__item">
              <label class="form__label" for="passwordConfirm">Confirm password *</label>
              <input class="form__input" :type="showPassword ? 'text' : 'password'" autocomplete="new-password"
                v-model="formData.passwordConfirm" id="passwordConfirm" />
              <p class="field-error" v-if="stepErrors.passwordConfirm">
                {{ stepErrors.passwordConfirm }}
              </p>
            </div>
          </fieldset>

          <p class="submit-error" v-if="submitError">{{ submitError }}</p>

          <nav class="form-nav">
            <button type="button" class="secondary-button" v-if="currentStep > 0" @click="previousStep()">
              Back
            </button>
            <button type="submit" class="primary-button">
              {{ currentStep === steps.length - 1 ? "Complete sign-up" : "Continue" }}
            </button>
          </nav>
        </form>
      </template>
    </div>
  </div>
</template>
<script>
import SpinnerLoader from "../../components/LoadingSpinning";
import TenantLogo from "@/components/TenantLogo";
import { getTenantName } from "@/config/tenant";
import { Users } from "@/services/user-service.js";
import { Trialists } from "@/services/trialist-service.js";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { Teams } from "@/services/teams-service.js";
import ImageUploader from "../../components/ImageUploader";
import MedicalConditionsEditor from "@/components/MedicalInfo/MedicalConditionsEditor.vue";
import moment from "moment";

const CLUB_NAME_OVERRIDES = {
  "surfSoccer-2085XH": "The big test",
  "dfc-2085XH": "Dundee",
  "hafc-2085XH": "Hamilton Academical",
  "kfc-2085XH": "Kilmarnock FC",
};

export default {
  name: "TrialistsExternalSignUp",
  components: {
    SpinnerLoader,
    TenantLogo,
    VueDatePicker,
    ImageUploader,
    MedicalConditionsEditor,
  },
  data() {
    return {
      loadingStuff: false,
      pageState: "loading",
      inviteStatus: "",
      currentStep: 0,
      steps: ["Player", "Contact", "Medical & consent", "Account"],
      stepErrors: {},
      submitError: "",
      showPassword: false,
      emailAlreadyExists: false,
      clubId: "",
      leadId: 0,
      invite: {
        teamName: "",
        teamId: "",
        trial_length: "",
        trial_start_date: "",
        player_name: "",
      },
      positions: [
        "Goalkeeper",
        "Right Full-back (Wingback)",
        "Left Full-back (Wingback)",
        "Center-back left",
        "Center-back right",
        "Defensive Midfielder",
        "Centre midfield",
        "Centre Defensive Midfield",
        "Right Midfielder (Winger)",
        "Left Midfielder (Winger)",
        "Attacking Midfielder",
        "Center Forward (Striker) left",
        "Center Forward (Striker) right",
        "I played more than one position",
      ],
      teamData: [],
      formData: {
        name: "",
        dob: "",
        position: "",
        preferred_foot: "Right",
        previous_club: "",
        userImage: "",
        teamId: "",
        trial_length: "6",
        trial_start_date: "",
        parent_name: "",
        phone: "",
        address: "",
        post_code: "",
        emergency_contact_one: "",
        emergency_contact_two: "",
        place_of_birth: "",
        country_of_birth: "",
        nationality: "",
        medical: "",
        medicalInfo: { hasConditions: null, conditions: [], notes: "" },
        permission: false,
        guardianConfirm: false,
        email: "",
        password: "",
        passwordConfirm: "",
        coach_id: 0,
        coach_name: "",
      },
      imageFile: null,
      image: {
        imageName: "",
        path: "",
        file: null,
      },
    };
  },
  computed: {
    inviteToken() {
      return this.$route.params.token !== undefined
        ? this.$route.params.token
        : "";
    },
    legacyMode() {
      return this.inviteToken === "";
    },
    clubName() {
      // Each site is built for one club, so the baked-in tenant name is the
      // most accurate display name (e.g. "Falkirk F.C.").
      const tenantName = getTenantName();
      if (tenantName) {
        return tenantName;
      }
      if (CLUB_NAME_OVERRIDES[this.clubId] !== undefined) {
        return CLUB_NAME_OVERRIDES[this.clubId];
      }
      if (this.clubId === "") {
        return "Your club";
      }
      // Club ids look like "Riverside Athletic-2085XH" — the display name
      // is everything before the final "-suffix".
      return this.clubId.replace(/-[^-]*$/, "");
    },
    invalidHeading() {
      if (this.inviteStatus === "used") {
        return "This invitation has already been used";
      }
      if (this.inviteStatus === "expired") {
        return "This invitation has expired";
      }
      return "This invitation link isn't valid";
    },
    invalidMessage() {
      if (this.inviteStatus === "used") {
        return "Someone has already completed the sign-up for this invitation.";
      }
      if (this.inviteStatus === "expired") {
        return "Invitation links work for 14 days and this one has now lapsed.";
      }
      return "The link may have been copied incorrectly.";
    },
  },
  async created() {
    if (this.legacyMode === false) {
      await this.loadInvite();
    } else {
      await this.loadLegacyClub();
    }
  },
  methods: {
    async loadInvite() {
      this.loadingStuff = true;
      const responce = await Trialists.find({
        action: "getTrialistInviteByToken",
        token: this.inviteToken,
      }).catch(() => null);
      this.loadingStuff = false;

      if (responce === null || responce.status !== 200) {
        this.inviteStatus = "invalid";
        this.pageState = "invalid";
        return;
      }

      const invite = responce.data;
      if (invite.status !== "valid") {
        this.inviteStatus = invite.status;
        this.pageState = "invalid";
        return;
      }

      this.clubId = invite.clubId;
      this.leadId = invite.leadId !== null ? invite.leadId : 0;
      this.invite.teamName = invite.teamName !== null ? invite.teamName : "";
      this.invite.teamId = invite.teamId !== null ? invite.teamId : "";
      this.invite.trial_length =
        invite.trial_length !== null ? invite.trial_length : "";
      this.invite.trial_start_date =
        invite.trial_start_date !== null ? invite.trial_start_date : "";

      if (invite.player_name !== null && invite.player_name !== "") {
        this.formData.name = invite.player_name;
      }
      if (invite.teamId !== null) {
        this.formData.teamId = invite.teamId;
        await this.loadCoachForTeam(invite.teamId);
      }
      if (invite.trial_length !== null) {
        this.formData.trial_length = String(invite.trial_length);
      }
      if (invite.trial_start_date !== null) {
        this.formData.trial_start_date = invite.trial_start_date;
      }

      this.pageState = "form";
    },
    async loadLegacyClub() {
      this.loadingStuff = true;
      const clubExists = await this.checkTeamIdexists();
      if (clubExists === false) {
        this.loadingStuff = false;
        this.inviteStatus = "invalid";
        this.pageState = "invalid";
        return;
      }

      this.clubId = this.$route.params.clubId;
      this.leadId =
        this.$route.params.trialistId !== undefined &&
          this.$route.params.trialistId !== "0"
          ? this.$route.params.trialistId
          : 0;
      this.teamData = await this.populateTeamData();
      this.loadingStuff = false;
      this.pageState = "form";
    },
    async loadCoachForTeam(teamId) {
      const responce = await Teams.find({
        teamId: teamId,
        action: "getAllCoachesAttachedToTeam",
      }).catch(() => null);

      if (
        responce !== null &&
        Array.isArray(responce.data) &&
        responce.data.length > 0
      ) {
        this.formData.coach_id = responce.data[0].id;
        this.formData.coach_name = responce.data[0].name;
      }
    },
    teamSelected() {
      if (this.formData.teamId !== "") {
        this.loadCoachForTeam(this.formData.teamId);
      }
    },
    async checkTeamIdexists() {
      const responce = await Users.find({
        clubId: this.$route.params.clubId,
        action: "checkTeamIdexists",
      }).catch(() => null);
      return responce !== null && responce.data.length > 0;
    },
    async populateTeamData() {
      const responce = await Teams.find({
        clubId: this.$route.params.clubId,
        action: "populateTeamData",
      }).catch(() => null);
      return responce !== null && responce.data.length > 0
        ? responce.data
        : [];
    },
    async checkIfEmailExists(value) {
      const responce = await Users.find({
        email: value,
        action: "checkEmailexists",
      }).catch(() => null);
      this.emailAlreadyExists =
        responce !== null && responce.data.length > 0;
      if (this.emailAlreadyExists) {
        this.stepErrors = {
          ...this.stepErrors,
          email:
            "An account already exists with this email address. Please use a different one.",
        };
      } else if (this.stepErrors.email) {
        const errors = { ...this.stepErrors };
        delete errors.email;
        this.stepErrors = errors;
      }
    },
    requiredEmailValidation(value) {
      const validRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return validRegex.test(value);
    },
    checkChangeVlaidation(value, elementName) {
      if (elementName === "email" && value !== "") {
        this.checkIfEmailExists(value);
      }
    },
    validateCurrentStep() {
      const errors = {};

      if (this.currentStep === 0) {
        if (this.formData.name.trim() === "") {
          errors.name = "Please enter the player's name.";
        }
        if (this.formData.dob === "" || this.formData.dob === null) {
          errors.dob = "Please enter the player's date of birth.";
        }
        if (this.legacyMode && this.formData.teamId === "") {
          errors.teamId = "Please choose a team.";
        }
      }

      if (this.currentStep === 1) {
        if (this.formData.parent_name.trim() === "") {
          errors.parent_name = "Please enter a parent or guardian name.";
        }
        if (this.formData.phone.trim() === "") {
          errors.phone = "Please enter a contact phone number.";
        }
        if (this.formData.emergency_contact_one.trim() === "") {
          errors.emergency_contact_one =
            "Please give at least one emergency contact.";
        }
      }

      if (this.currentStep === 2) {
        const m = this.formData.medicalInfo || {};
        if (m.hasConditions !== 1 && m.hasConditions !== 0) {
          errors.medical = "Please answer the medical information question (Yes or No).";
        } else if (
          m.hasConditions === 1 &&
          (m.conditions || []).some(
            (c) => !(c.condition_name && c.condition_name.trim()) && !(c.medication && c.medication.trim()),
          )
        ) {
          errors.medical = "Please name each medical condition (or remove the empty one).";
        }
        if (this.formData.guardianConfirm !== true) {
          errors.guardianConfirm =
            "Please confirm you're the parent or guardian (or the player is 16+).";
        }
      }

      if (this.currentStep === 3) {
        if (this.requiredEmailValidation(this.formData.email) === false) {
          errors.email = "Please enter a valid email address.";
        } else if (this.emailAlreadyExists) {
          errors.email =
            "An account already exists with this email address. Please use a different one.";
        }
        if (this.formData.password.length < 8) {
          errors.password = "Passwords need at least 8 characters.";
        }
        if (this.formData.password !== this.formData.passwordConfirm) {
          errors.passwordConfirm = "The passwords don't match.";
        }
      }

      this.stepErrors = errors;
      return Object.keys(errors).length === 0;
    },
    nextStep() {
      if (this.validateCurrentStep() === false) {
        return;
      }
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep = this.currentStep + 1;
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      this.submitSignUp();
    },
    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep = this.currentStep - 1;
        this.stepErrors = {};
      }
    },
    clearImageDataFromUpload() {
      this.imageFile = null;
      this.image = {
        imageName: "",
        path: "",
        file: null,
      };
    },
    imageUploadObjectDataChanged(data) {
      this.image = data;
    },
    imageUploadFileDataChnged(data) {
      this.imageFile = data;
    },
    formatDateForHuman(date) {
      return moment(date).format("MMMM Do YYYY");
    },
    fotmatDatesForDb(date) {
      if (date === "" || date === null) {
        return "";
      }
      return moment(date).format("YYYY-MM-DD");
    },
    formatAgeGroupForSQL(dob) {
      const d = new Date(dob);
      return d.getFullYear();
    },
    async submitSignUp() {
      this.submitError = "";
      this.loadingStuff = true;

      const permissionValue = this.formData.permission === true ? 1 : 0;
      const trialStartDate = this.fotmatDatesForDb(
        this.formData.trial_start_date,
      );
      const ageGroup = this.formAgeGroup();

      const payload = {
        action: "addTrialist",
        inviteToken: this.inviteToken,
        name: this.formData.name,
        dob: this.formData.dob,
        position: this.formData.position,
        trial_length: this.formData.trial_length,
        trial_start_date: trialStartDate,
        preferred_foot: this.formData.preferred_foot,
        phone: this.formData.phone,
        email: this.formData.email,
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
        outcome: 0,
        clubId: this.clubId,
        teamId: this.formData.teamId,
        coach_name: this.formData.coach_name,
        coach_id: this.formData.coach_id,
        age_group: ageGroup,
        permission: permissionValue,
        userType: "User",
        registerUser: {
          img: "",
          name: this.formData.name,
          email: this.formData.email,
          password: this.formData.password,
          dob: this.formData.dob,
          position: this.formData.position,
          phone: this.formData.phone,
          emergency_contact_one: this.formData.emergency_contact_one,
          emergency_contact_two: this.formData.emergency_contact_two,
          userType: "User",
          action: "register",
          teamId: this.formData.teamId,
          permission: permissionValue,
          clubId: this.clubId,
          coach_id: this.formData.coach_id,
          team_module: 1,
          preferred_foot: this.formData.preferred_foot === "Left" ? 0 : 1,
          joined_date: trialStartDate,
          medical: this.formData.medicalInfo.notes || this.formData.medical,
          has_medical_conditions: this.formData.medicalInfo.hasConditions,
          medical_conditions:
            this.formData.medicalInfo.hasConditions === 1 ? this.formData.medicalInfo.conditions : [],
          parent_name: this.formData.parent_name,
          age_group: ageGroup,
          address: this.formData.address,
          place_of_birth: this.formData.place_of_birth,
          country_of_birth: this.formData.country_of_birth,
          nationality: this.formData.nationality,
          previous_club: this.formData.previous_club,
          post_code: this.formData.post_code,
        },
      };

      if (this.leadId !== 0) {
        payload.leadId = this.leadId;
      }
      if (this.image.file !== null && this.imageFile !== null) {
        payload.fileName = this.imageFile.imageName;
        payload.imageFile = this.image;
      }

      const responce = await Trialists.find(payload).catch((error) => {
        return error !== undefined && error.response !== undefined
          ? error.response
          : null;
      });
      this.loadingStuff = false;

      if (responce !== null && responce.status === 200) {
        this.pageState = "done";
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (responce !== null && responce.status === 403) {
        this.inviteStatus = "invalid";
        this.pageState = "invalid";
        return;
      }

      this.submitError =
        "Something went wrong sending your details. Please check your connection and try again — nothing has been lost.";
    },
    formAgeGroup() {
      return this.formatAgeGroupForSQL(this.formData.dob);
    },
  },
};
</script>
<style lang="scss" scoped>
.signup-page {
  min-height: 100vh;
  background: rgba($primary-color, 0.05);
}

.signup-shell {
  max-width: 40rem;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
}

.signup-header {
  text-align: center;
  margin-bottom: 1.5rem;

  .signup-logo {
    display: block;
    height: 72px;
    width: auto;
    max-width: 200px;
    object-fit: contain;
    margin: 0 auto 0.85rem;
  }

  .signup-club {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: bold;
    color: $primary-color;
    margin-bottom: 0.25rem;
  }

  .signup-title {
    font-size: 1.75rem;
    margin: 0;
    color: $primary-color;
  }
}

.signup-card {
  background: #fff;
  border: 1px solid rgba($primary-color, 0.15);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;

  legend {
    font-size: 1.15rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  fieldset {
    border: none;
    padding: 0;
    margin: 0;
  }
}

.signup-card--message {
  text-align: center;

  h2 {
    margin-bottom: 1rem;
  }

  .trial-summary {
    margin: 1.5rem auto;
    max-width: 22rem;
    text-align: left;
  }

  .primary-button {
    display: inline-block;
    margin-top: 1rem;
    text-decoration: none;
  }
}

.trial-card h2 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
}

.trial-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1.25rem;
  margin: 0;

  dt {
    font-weight: bold;
    color: #4c5a51;
  }

  dd {
    margin: 0;
  }
}

.steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  counter-reset: step;

  .steps__item {
    counter-increment: step;
    font-size: 0.85rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: #e4eae6;
    color: #59695e;

    &::before {
      content: counter(step) ". ";
    }
  }

  .steps__item--active {
    background: $primary-color;
    color: #fff;
    font-weight: bold;
  }

  .steps__item--done {
    background: rgba($primary-color, 0.15);
    color: $primary-color;
  }
}

.form__item {
  margin-bottom: 1rem;
}

.form__label {
  display: block;
  font-weight: bold;
  margin-bottom: 0.35rem;
}

.form__input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid #c6d0c9;
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: 2px solid $primary-color;
    outline-offset: 1px;
  }
}

.form__textarea {
  resize: vertical;
}

select {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid #c6d0c9;
  border-radius: 6px;
  font-size: 1rem;
  background: #fff;
}

.danger-outline {
  border-color: #d85c64;
}

.field-hint {
  font-size: 0.85rem;
  color: #59695e;
  margin: 0.25rem 0 0.5rem;
}

.field-error {
  color: #c03540;
  font-size: 0.85rem;
  margin: 0.35rem 0 0;
}

.submit-error {
  background: #ffe8ec;
  border: 1px solid #d85c64;
  color: #a12833;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin: 1rem 0 0;
}

.form__item--checkbox {
  .checkbox-label {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
    font-weight: normal;

    input {
      margin-top: 0.3rem;
      flex-shrink: 0;
    }
  }
}

.form-nav {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;

  .primary-button {
    margin-left: auto;
  }
}

.primary-button {
  background: $primary-color;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.65rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    filter: brightness(0.9);
  }
}

.secondary-button {
  background: #fff;
  color: $primary-color;
  border: 1px solid $primary-color;
  border-radius: 6px;
  padding: 0.65rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
}

@media (max-width: 480px) {
  .signup-card {
    padding: 1rem;
  }
}
</style>
